import { useCallback } from "react";
import { useMedplum } from "@medplum/react";

import { ensureActiveSession } from "@/utils/auth/ensureActiveSession";
import { getApiEndpoint, getApiAuthHeaders } from "@/utils/api/getApiEndpoint";

/**
 * Options for patient creation notification.
 * @agentic connections
 */
export type NotifyPatientCreatedOptions = {
  source?: "connections" | "fax" | "cli" | "api";
  practice?: string;
  referringPractitioner?: string;
};

/**
 * Result type for useNotifyPatientCreated hook.
 * @agentic connections
 */
export type UseNotifyPatientCreatedResult = {
  notifyPatientCreated: (
    patientId: string,
    options?: NotifyPatientCreatedOptions,
  ) => Promise<void>;
};

/**
 * Sends patient creation notification to Google Chat via API; fire-and-forget.
 * @agentic connections
 */
export function useNotifyPatientCreated(): UseNotifyPatientCreatedResult {
  const medplum = useMedplum();

  const notifyPatientCreated = useCallback(
    async (
      patientId: string,
      options: NotifyPatientCreatedOptions = {},
    ): Promise<void> => {
      try {
        // Ensure session is active and get a fresh access token
        const accessToken = await ensureActiveSession(medplum);

        const apiUrl = getApiEndpoint("/api/v1/patient/notify-created");

        const response = await fetch(apiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-Medplum-Authorization": `Bearer ${accessToken}`,
            ...getApiAuthHeaders(),
          },
          body: JSON.stringify({
            patientId,
            source: options.source || "connections",
            practice: options.practice,
            referringPractitioner: options.referringPractitioner,
          }),
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          // Notification is best-effort: log at info so misconfigured
          // dev/staging envs (e.g., missing Google Chat webhook) don't
          // surface as PR-review-noise warnings.
          console.info("Patient notification request failed:", errorData);
          return;
        }

        const result = await response.json();

        if (result.notificationSent) {
          console.info("Patient creation notification sent", { patientId });
        } else {
          console.info("Patient notification skipped:", result.reason);
        }
      } catch (error) {
        // Best-effort path; info-level so transient endpoint failures
        // don't pollute the console for unrelated workflows.
        console.info("Failed to send patient creation notification:", error);
      }
    },
    [medplum],
  );

  return {
    notifyPatientCreated,
  };
}
